import {
  Button,
  DropdownMultiSelect,
  X,
} from "@ews-admin/global-design-system";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  featuresToString,
  stringToFeatures,
} from "../../atoms/FeatureListEditor";
import { formatMoney } from "../../../utils/money-utils";
import { FeatureOption } from "../../../types/feature.definition";
import { getFeatureLabel } from "../../../utils/feature-label-utils";

export interface PlanField {
  key: string;
  label: string;
  note?: string;
  suffix?: string;
  min?: number;
  step?: number;
}

export interface PlanEditorValues {
  [key: string]: number | string | null;
  features: string | null;
}

interface PlanMeta {
  accentBg: string;
  badge: string;
  label: string;
}

interface Props {
  open: boolean;
  planMeta: PlanMeta;
  planLabel: string;
  fields: PlanField[];
  values: PlanEditorValues;
  availableFeatures: FeatureOption[];
  saving: boolean;
  onSave: (values: PlanEditorValues) => void;
  onClose: () => void;
  previewConfig: {
    primaryPriceKey: string;
    primaryPriceSuffix?: string;
    secondaryPriceKey?: string;
    secondaryPriceSuffix?: string;
  };
}

export const PlanEditorPanel: React.FC<Props> = ({
  open,
  planMeta,
  planLabel,
  fields,
  values: initialValues,
  availableFeatures,
  saving,
  onSave,
  onClose,
  previewConfig,
}) => {
  const { t } = useTranslation();
  const [values, setValues] = useState<PlanEditorValues>(initialValues);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>(
    stringToFeatures(initialValues.features)
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setValues(initialValues);
    setSelectedFeatures(stringToFeatures(initialValues.features));
    setErrors({});
  }, [initialValues, open]);

  const setField = (key: string, raw: string) => {
    const num = parseFloat(raw);
    setValues(v => ({ ...v, [key]: isNaN(num) ? raw : num }));
    setErrors(e => ({ ...e, [key]: "" }));
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    for (const f of fields) {
      const val = values[f.key];
      const num = typeof val === "number" ? val : parseFloat(String(val));
      if (isNaN(num) || num < (f.min ?? 0)) {
        next[f.key] = t("admin.planEditor.validation.mustBeAtLeast", {
          min: f.min ?? 0,
        });
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    onSave({ ...values, features: featuresToString(selectedFeatures) });
  };

  const featureOptions = availableFeatures.map(f => ({
    value: f.code,
    label: getFeatureLabel(f.code, availableFeatures, t),
  }));

  const primaryPrice = values[previewConfig.primaryPriceKey];
  const secondaryPrice = previewConfig.secondaryPriceKey
    ? values[previewConfig.secondaryPriceKey]
    : null;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/30 z-40 transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={onClose}
      />

      {/* Slide-in panel */}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-lg bg-white shadow-2xl z-50 flex flex-col transform transition-transform duration-300 ease-in-out ${open ? "translate-x-0" : "translate-x-full"}`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between px-6 py-4 border-b border-gray-200 ${planMeta.accentBg}`}
        >
          <div className="flex items-center gap-3">
            <span
              className={`text-xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${planMeta.badge}`}
            >
              {t("admin.planEditor.editPlan")}
            </span>
            <h2 className="text-lg font-bold text-white">{planLabel}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md text-white/80 hover:text-white hover:bg-white/20 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-8">
          {/* Pricing fields */}
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">
              {t("admin.planEditor.sectionPricing")}
            </h3>
            <div className="space-y-4">
              {fields.map(f => (
                <div key={f.key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {f.label}
                    {f.note && (
                      <span className="ml-1 text-xs font-normal text-gray-400">
                        {f.note}
                      </span>
                    )}
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={String(values[f.key] ?? "")}
                      onChange={e => setField(f.key, e.target.value)}
                      min={f.min ?? 0}
                      step={f.step ?? 1}
                      className={`w-full rounded-md border px-3 py-2 text-sm text-gray-900 pr-16 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors[f.key] ? "border-red-400" : "border-gray-300"}`}
                    />
                    {f.suffix && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                        {f.suffix}
                      </span>
                    )}
                  </div>
                  {errors[f.key] && (
                    <p className="mt-1 text-xs text-red-500">{errors[f.key]}</p>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Features */}
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">
              {t("admin.planEditor.sectionFeatures")}
            </h3>
            {featureOptions.length === 0 ? (
              <p className="text-sm text-gray-400 italic">
                {t("admin.planEditor.noFeaturesAvailable")}
              </p>
            ) : (
              <DropdownMultiSelect
                name="features"
                options={featureOptions}
                value={selectedFeatures}
                onChange={setSelectedFeatures}
                placeholder={t("admin.planEditor.featuresPlaceholder")}
                searchPlaceholder={t("admin.planEditor.featuresSearch")}
              />
            )}
          </section>

          {/* Live preview */}
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">
              {t("admin.planEditor.sectionPreview")}
            </h3>
            <div className="rounded-xl border border-gray-200 overflow-hidden shadow-sm">
              <div className={`h-1.5 ${planMeta.accentBg}`} />
              <div className="p-5">
                <span
                  className={`inline-block text-xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full mb-2 ${planMeta.badge}`}
                >
                  {planLabel}
                </span>
                <p className="text-2xl font-bold text-gray-900">
                  {formatMoney(Number(primaryPrice))}
                  {previewConfig.primaryPriceSuffix && (
                    <span className="text-sm font-normal text-gray-400 ml-1">
                      {previewConfig.primaryPriceSuffix}
                    </span>
                  )}
                </p>
                {secondaryPrice !== null && (
                  <p className="text-sm text-gray-500 mt-0.5">
                    +{formatMoney(Number(secondaryPrice))}{" "}
                    {previewConfig.secondaryPriceSuffix}
                  </p>
                )}
                {selectedFeatures.length > 0 && (
                  <ul className="mt-4 space-y-1.5">
                    {selectedFeatures.map((code, i) => {
                      const featureLabel = getFeatureLabel(
                        code,
                        availableFeatures,
                        t
                      );
                      return (
                        <li
                          key={i}
                          className="flex items-center gap-2 text-sm text-gray-700"
                        >
                          <span
                            className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${planMeta.accentBg}`}
                          >
                            <svg
                              className="w-2.5 h-2.5 text-white"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={3}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          </span>
                          {featureLabel}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
          >
            {t("common.cancel")}
          </button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? t("common.saving") : t("admin.planEditor.savePlan")}
          </Button>
        </div>
      </div>
    </>
  );
};
